import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useLocation } from 'react-router-dom';
import { GripVertical, ChevronsRight, Minus, X, Check, Eye, ArrowRight, MessageCircleQuestion } from 'lucide-react';
import apiClient from '../../services/apiClient';
import { appStorage } from '../../lib/safeStorage';
import SpotlightTour from '../SpotlightTour';
import { getGuide } from '../../lib/guideRegistry';
import {
  getActiveGuide, subscribeGuide, closeGuide, setGuideState,
  setGuidePosition, setGuideEvent, getManualDone, setManualDone, setGuideIdentity,
} from '../../lib/guideStore';

const GOLD = '#D4AF37';
const CARD_W = 360;
const Z = 1200; // above page content, below Spotlight (9998), modals & toasts

// CSS-only travelling gold border (one rotation ~9s, low opacity). No JS timer.
// Static faint gold under prefers-reduced-motion.
const STYLE_ID = 'staffed-guide-card-style';
function ensureStyle() {
  if (typeof document === 'undefined' || document.getElementById(STYLE_ID)) return;
  const el = document.createElement('style');
  el.id = STYLE_ID;
  el.textContent = `
@property --sg-angle { syntax: '<angle>'; initial-value: 0deg; inherits: false; }
.staffed-guide-card { position: relative; }
.staffed-guide-card::before {
  content: ''; position: absolute; inset: 0; border-radius: 18px; padding: 1px;
  background: conic-gradient(from var(--sg-angle), rgba(212,175,55,0) 0deg, rgba(212,175,55,0.55) 60deg, rgba(212,175,55,0) 140deg, rgba(212,175,55,0) 360deg);
  -webkit-mask: linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0);
  -webkit-mask-composite: xor; mask-composite: exclude;
  pointer-events: none; animation: staffed-guide-spin 9s linear infinite;
}
@keyframes staffed-guide-spin { to { --sg-angle: 360deg; } }
@media (prefers-reduced-motion: reduce) {
  .staffed-guide-card::before { animation: none; background: none; border: 1px solid rgba(212,175,55,0.28); }
}`;
  document.head.appendChild(el);
}

const clamp = (v, min, max) => Math.max(min, Math.min(max, v));

export default function HowToCard({ identity = null }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [active, setActive] = useState(() => getActiveGuide());
  const [server, setServer] = useState(null); // {steps:[{done,reason,manual}]} | 'error' | null
  const [manual, setManual] = useState({});
  const [pos, setPos] = useState(null);
  const [spotlightSteps, setSpotlightSteps] = useState([]);
  const [followedName, setFollowedName] = useState('');
  const [notFound, setNotFound] = useState(false);
  const cardRef = useRef(null);
  const dragRef = useRef(null);

  useEffect(() => { ensureStyle(); }, []);
  useEffect(() => subscribeGuide((s) => setActive(s ? { ...s } : null)), []);

  // ONE source of truth for the signed-in account: hand the identity to the
  // guide store so its browser-storage keys are namespaced per account. On
  // login / logout / switch this re-reads under the new identity, so a guide
  // from a previous account never renders here.
  useEffect(() => {
    setGuideIdentity(identity);
    setActive(getActiveGuide() || null);
  }, [identity]);

  const guide = active ? getGuide(active.guideId) : null;
  const eventId = active?.eventId || null;

  // BLOCK S7b 3 — the guide follows the event it was started on. If the admin
  // opens a DIFFERENT event, keep the guide open (progress still reads the
  // followed event) and offer an explicit "Switch to this event". Never auto-switch.
  const routeEventId = (location.pathname.match(/^\/event\/([^/]+)/) || [])[1] || null;
  const mismatch = !!(eventId && routeEventId && routeEventId !== eventId);

  useEffect(() => {
    if (!mismatch) { setFollowedName(''); return undefined; }
    if (active?.eventName) { setFollowedName(active.eventName); return undefined; }
    let cancelled = false;
    apiClient.get(`/leads/events/${eventId}`).then((r) => {
      if (cancelled) return;
      const full = String(r.data?.fullName || r.data?.event?.fullName || '').trim();
      setFollowedName(full ? full.split(/\s+/)[0] : '');
    }).catch(() => { if (!cancelled) setFollowedName(''); });
    return () => { cancelled = true; };
  }, [mismatch, eventId, active?.eventName]);

  // Position: from store, else a sensible bottom-right default. Clamped to view.
  useEffect(() => {
    if (!guide) return;
    const w = window.innerWidth;
    const stored = active?.pos;
    // BLOCK S7b 4 — first-time default sits top-right, clear of the event
    // header buttons (top) and the chat bubble (bottom-right). Existing stored
    // positions are respected; drag/tuck/minimise are unchanged.
    const def = { x: w - CARD_W - 24, y: 104 };
    const p = stored || def;
    setPos({ x: clamp(p.x, 8, Math.max(8, w - CARD_W - 8)), y: clamp(p.y, 8, Math.max(8, window.innerHeight - 120)) });
    setManual(getManualDone(guide.id, eventId));
  }, [guide?.id, eventId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Keep fully on screen when the window resizes.
  useEffect(() => {
    const onResize = () => setPos((p) => (p ? {
      x: clamp(p.x, 8, Math.max(8, window.innerWidth - CARD_W - 8)),
      y: clamp(p.y, 8, Math.max(8, window.innerHeight - 120)),
    } : p));
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Read progress — deduped so overlapping triggers don't stack up. Cheap: one
  // GET, skipped when already in flight or when there's no active guide.
  const inFlightRef = useRef(false);
  const loadProgress = useCallback(async () => {
    if (!guide || inFlightRef.current) return;
    inFlightRef.current = true;
    try {
      const res = await apiClient.get(`/guides/${guide.id}/progress`, {
        params: eventId ? { eventId } : {},
      });
      setServer(res.data && Array.isArray(res.data.steps) ? res.data : 'error');
    } catch { setServer('error'); }
    finally { inFlightRef.current = false; }
  }, [guide?.id, eventId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Re-read: on open/guide change, on route change, on a section/tab change
  // (?section=…, item 2a), and when switching to another event (eventId, 2b).
  useEffect(() => { loadProgress(); }, [loadProgress, location.pathname, location.search]);

  // BLOCK S7c 1/2 — the ONE shared "event data changed" signal (every workspace
  // refresh path emits it). The card just re-reads; it never triggers a refresh.
  useEffect(() => {
    const onRefresh = () => loadProgress();
    window.addEventListener('staffed:event:changed', onRefresh);
    return () => window.removeEventListener('staffed:event:changed', onRefresh);
  }, [loadProgress]);

  // 2d — re-read when the card is opened/restored from tucked/minimised.
  const cardState = active?.state || 'open';
  useEffect(() => { if (cardState === 'open') loadProgress(); }, [cardState, loadProgress]);

  // 2c — re-read when the tab regains focus after 30s+ away.
  const hiddenAtRef = useRef(0);
  useEffect(() => {
    const onVis = () => {
      if (document.visibilityState === 'hidden') { hiddenAtRef.current = Date.now(); return; }
      if (hiddenAtRef.current && Date.now() - hiddenAtRef.current >= 30000) loadProgress();
      hiddenAtRef.current = 0;
    };
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, [loadProgress]);

  // Merge registry steps + server ticks + local manual ticks.
  const steps = useMemo(() => {
    if (!guide) return [];
    const srv = server && server !== 'error' ? server.steps : [];
    return guide.steps.map((s, i) => {
      const isManual = !!s.manual;
      const serverDone = srv[i] ? !!srv[i].done : false;
      const done = isManual ? !!manual[i] : serverDone;
      return { ...s, index: i, manual: isManual, done, reason: srv[i]?.reason || '' };
    });
  }, [guide, server, manual]);

  const total = steps.length;
  const doneCount = steps.filter((s) => s.done).length;
  const allDone = total > 0 && doneCount === total;
  const currentIndex = useMemo(() => {
    const i = steps.findIndex((s) => !s.done);
    return i === -1 ? total - 1 : i;
  }, [steps, total]);
  const stepNo = Math.min(currentIndex + 1, total);

  const fillRoute = (route) => (route || '').replace(':eventId', eventId || '');

  // Open the tab/section that actually CONTAINS the step's target element.
  // Event routes carry the section in the URL (?section=…) which the workspace
  // reads to open the tab. The Templates page owns its tab in local state, so
  // we persist the choice and nudge any already-mounted page to switch.
  const ensureSectionOpen = (step) => {
    const route = fillRoute(step?.target?.route || '');
    const section = step?.target?.section;
    if (!section) return;
    if (route.startsWith('/templates')) {
      try { appStorage.setItem('templatesActiveType', section); } catch { /* no-op */ }
      try { window.dispatchEvent(new CustomEvent('staffed:templates:set-tab', { detail: { tab: section } })); } catch { /* no-op */ }
    }
  };

  // Spotlight the target once it exists. Retries briefly so a freshly-opened
  // tab/page has time to render. If it still can't be found, show a quiet note
  // instead of doing nothing silently.
  const spotlightRetryRef = useRef(0);
  const runSpotlight = (step) => {
    if (!step?.spotlight) return;
    setNotFound(false);
    setSpotlightSteps([{ target: step.spotlight, title: step.title, text: step.detail }]);
    spotlightRetryRef.current += 1;
    const token = spotlightRetryRef.current;
    let tries = 0;
    const attempt = () => {
      if (token !== spotlightRetryRef.current) return; // superseded by a newer click
      tries += 1;
      let visible = false;
      try {
        const el = document.querySelector(step.spotlight);
        visible = !!(el && el.getBoundingClientRect().width > 0 && el.getBoundingClientRect().height > 0);
      } catch { visible = false; }
      if (visible) {
        try { window.dispatchEvent(new CustomEvent('staffed:tour:staffed-guide-spotlight')); } catch { /* no-op */ }
        return;
      }
      if (tries < 15) { setTimeout(attempt, 200); return; }
      setNotFound(true);
      setTimeout(() => setNotFound(false), 4000);
    };
    setTimeout(attempt, 140);
  };

  const takeMeThere = (step) => {
    if (!step?.target?.route) return;
    const path = fillRoute(step.target.route);
    const section = step.target.section;
    const isEvent = path.startsWith('/event/');
    // Event tabs live in the URL; other pages (Templates) own the tab in state.
    const dest = isEvent && section ? `${path}?section=${section}` : path;
    const here = `${location.pathname}${location.search}`;
    if (dest !== here) navigate(dest);
    // Open the correct tab/section AND spotlight — even when already on the
    // route, so this is never a silent no-op.
    ensureSectionOpen(step);
    runSpotlight(step);
  };

  const showMe = (step) => {
    if (!step?.spotlight) return;
    // Make sure the target's tab/section is open first, so its element exists.
    ensureSectionOpen(step);
    runSpotlight(step);
  };

  const askAboutStep = (step) => {
    try {
      window.dispatchEvent(new CustomEvent('staffed:assistant:open', {
        detail: { prefill: `About "${guide.title}" — step ${step.index + 1}: ${step.title}. ` },
      }));
    } catch { /* no-op */ }
  };

  const toggleManual = (step) => {
    const next = setManualDone(guide.id, eventId, step.index, !manual[step.index]);
    setManual({ ...next });
  };

  // ── Drag by the handle (pointer events: mouse + touch) ──
  const onHandlePointerDown = (e) => {
    if (!pos) return;
    const startX = e.clientX; const startY = e.clientY;
    const origin = { ...pos };
    dragRef.current = { startX, startY, origin };
    try { e.target.setPointerCapture(e.pointerId); } catch { /* no-op */ }
    const onMove = (ev) => {
      const d = dragRef.current; if (!d) return;
      const nx = clamp(d.origin.x + (ev.clientX - d.startX), 8, Math.max(8, window.innerWidth - CARD_W - 8));
      const ny = clamp(d.origin.y + (ev.clientY - d.startY), 8, Math.max(8, window.innerHeight - 120));
      setPos({ x: nx, y: ny });
    };
    const onUp = () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      setPos((p) => { if (p) setGuidePosition(p); return p; });
      dragRef.current = null;
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
  };

  // Keyboard: arrow keys nudge the card 16px while the handle is focused.
  const onHandleKeyDown = (e) => {
    if (!pos) return;
    const step = 16;
    let { x, y } = pos;
    if (e.key === 'ArrowLeft') x -= step;
    else if (e.key === 'ArrowRight') x += step;
    else if (e.key === 'ArrowUp') y -= step;
    else if (e.key === 'ArrowDown') y += step;
    else return;
    e.preventDefault();
    const np = {
      x: clamp(x, 8, Math.max(8, window.innerWidth - CARD_W - 8)),
      y: clamp(y, 8, Math.max(8, window.innerHeight - 120)),
    };
    setPos(np); setGuidePosition(np);
  };

  if (!guide || !pos) return null;

  const state = active.state || 'open';
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 640;

  const spotlightMount = (
    <SpotlightTour tourId="staffed-guide-spotlight" steps={spotlightSteps} autoStart={false} />
  );

  // ── MINIMISED: pill bottom-left with a progress ring ──
  if (state === 'minimised') {
    const R = 11; const C = 2 * Math.PI * R;
    return createPortal(
      <>
        {spotlightMount}
        <button
          type="button"
          data-testid="howto-card-minimised"
          onClick={() => setGuideState('open')}
          style={{
            position: 'fixed', left: 20, bottom: 20, zIndex: Z, display: 'flex', alignItems: 'center', gap: 10,
            background: 'rgba(10,12,16,0.92)', border: `1px solid ${GOLD}55`, borderRadius: 9999,
            padding: '8px 14px 8px 10px', color: '#e9d9a0', fontSize: 12, fontWeight: 600, cursor: 'pointer',
            backdropFilter: 'blur(8px)', boxShadow: '0 10px 30px rgba(0,0,0,0.5)',
          }}
        >
          <svg width="28" height="28" viewBox="0 0 28 28" aria-hidden="true">
            <circle cx="14" cy="14" r={R} fill="none" stroke="rgba(255,255,255,0.14)" strokeWidth="3" />
            <circle cx="14" cy="14" r={R} fill="none" stroke={GOLD} strokeWidth="3" strokeLinecap="round"
              strokeDasharray={C} strokeDashoffset={C * (1 - doneCount / total)} transform="rotate(-90 14 14)" />
          </svg>
          <span>{guide.shortTitle} · step {stepNo} of {total}</span>
        </button>
      </>,
      document.body,
    );
  }

  // ── TUCKED: slim vertical tab on the nearest screen edge ──
  if (state === 'tucked') {
    const side = pos.x + CARD_W / 2 < window.innerWidth / 2 ? 'left' : 'right';
    return createPortal(
      <>
        {spotlightMount}
        <button
          type="button"
          data-testid="howto-card-tucked"
          onClick={() => setGuideState('open')}
          style={{
            position: 'fixed', top: clamp(pos.y, 60, window.innerHeight - 160), [side]: 0, zIndex: Z,
            writingMode: 'vertical-rl', transform: side === 'left' ? 'rotate(180deg)' : 'none',
            background: 'rgba(10,12,16,0.92)', border: `1px solid ${GOLD}55`,
            borderRadius: side === 'left' ? '10px 0 0 10px' : '0 10px 10px 0',
            padding: '14px 8px', color: '#e9d9a0', fontSize: 11, fontWeight: 700, letterSpacing: 1,
            cursor: 'pointer', boxShadow: '0 10px 30px rgba(0,0,0,0.5)',
          }}
        >
          Guide · {stepNo}/{total}
        </button>
      </>,
      document.body,
    );
  }

  // ── OPEN (full card). Bottom sheet on mobile. ──
  const frame = isMobile
    ? { left: 0, right: 0, bottom: 0, width: '100%', maxWidth: '100%', borderRadius: '18px 18px 0 0' }
    : { left: pos.x, top: pos.y, width: CARD_W, borderRadius: 18 };

  return createPortal(
    <>
      {spotlightMount}
      <div
        ref={cardRef}
        className="staffed-guide-card"
        data-testid="howto-card"
        style={{
          position: 'fixed', zIndex: Z, ...frame,
          background: 'linear-gradient(180deg,#12162a,#0a0e18)',
          border: '1px solid rgba(255,255,255,0.06)', color: '#fff',
          boxShadow: '0 24px 60px -18px rgba(0,0,0,0.8)', overflow: 'hidden',
        }}
      >
        {/* Header */}
        <div className="flex items-center gap-2 px-3.5 pt-3 pb-2 border-b border-white/[0.06]">
          <button
            type="button"
            aria-label="Move guide"
            data-testid="howto-drag-handle"
            onPointerDown={onHandlePointerDown}
            onKeyDown={onHandleKeyDown}
            className="text-white/40 hover:text-white/80 focus:outline-none focus-visible:text-[#E7C96A] cursor-grab active:cursor-grabbing"
            style={{ touchAction: 'none' }}
          >
            <GripVertical className="w-4 h-4" />
          </button>
          <div className="min-w-0 flex-1">
            <p className="text-[10px] uppercase tracking-[0.18em] font-semibold text-[#E7C75E]">Guide · step {stepNo} of {total}</p>
            <h4 className="text-[13px] font-semibold text-white truncate">{guide.title}</h4>
          </div>
          <button type="button" aria-label="Tuck to edge" data-testid="howto-tuck" onClick={() => setGuideState('tucked')} className="text-white/40 hover:text-white/80 p-0.5"><ChevronsRight className="w-4 h-4" /></button>
          <button type="button" aria-label="Minimise" data-testid="howto-minimise" onClick={() => setGuideState('minimised')} className="text-white/40 hover:text-white/80 p-0.5"><Minus className="w-4 h-4" /></button>
          <button type="button" aria-label="Close guide" data-testid="howto-close" onClick={() => closeGuide()} className="text-white/40 hover:text-white/80 p-0.5"><X className="w-4 h-4" /></button>
        </div>

        {/* Body */}
        <div className="px-3.5 py-3 max-h-[60vh] overflow-y-auto">
          {mismatch && (
            <div className="flex items-center gap-2 rounded-xl px-3 py-2 mb-2.5" style={{ background: 'rgba(212,175,55,0.08)', border: '1px solid rgba(212,175,55,0.22)' }} data-testid="howto-switch-event">
              <span className="text-[11.5px] text-white/70 flex-1 min-w-0 truncate">Following {followedName ? `${followedName}\u2019s` : 'another'} event</span>
              <button type="button" data-testid="howto-switch-event-btn" onClick={() => setGuideEvent(routeEventId, null)} className="shrink-0 text-[11.5px] font-semibold text-[#E7C96A] underline">Switch to this event</button>
            </div>
          )}
          {server === 'error' && (
            <p className="text-[11px] text-amber-300/80 mb-2" data-testid="howto-progress-error">Couldn&rsquo;t check progress — steps still work.</p>
          )}
          <ol className="space-y-1.5">
            {steps.map((s) => {
              const isCurrent = s.index === currentIndex && !allDone;
              return (
                <li key={s.index} data-testid={`howto-step-${s.index}`}
                  className={`rounded-xl px-3 py-2.5 ${isCurrent ? 'bg-[#D4AF37]/[0.08] border border-[#D4AF37]/25' : 'border border-transparent'}`}>
                  <div className="flex items-start gap-2.5">
                    <span className={`mt-0.5 w-4 h-4 rounded-full flex items-center justify-center shrink-0 ${s.done ? 'bg-[#22C55E]' : 'border border-white/25'}`}>
                      {s.done && <Check className="w-3 h-3 text-[#0B0F1A]" />}
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className={`text-[12.5px] font-medium ${s.done ? 'line-through text-white/40' : 'text-white/85'}`}>{s.title}</p>
                      {isCurrent && (
                        <>
                          <p className="text-[11.5px] text-white/55 mt-1 leading-relaxed">{s.detail}</p>
                          <div className="flex flex-wrap items-center gap-1.5 mt-2">
                            {s.spotlight && (
                              <button type="button" data-testid={`howto-show-me-${s.index}`} onClick={() => showMe(s)} className="inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-semibold text-[#E7C96A] border border-[#D4AF37]/30 hover:bg-[#D4AF37]/10"><Eye className="w-3 h-3" /> Show me</button>
                            )}
                            {s.target?.route && (
                              <button type="button" data-testid={`howto-take-me-${s.index}`} onClick={() => takeMeThere(s)} className="inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-semibold text-[#0B0F1A]" style={{ background: 'linear-gradient(180deg,#E7C75E,#D4AF37)' }}>Take me there <ArrowRight className="w-3 h-3" /></button>
                            )}
                            {s.manual && (
                              <button type="button" data-testid={`howto-mark-done-${s.index}`} onClick={() => toggleManual(s)} className="inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-semibold text-white/70 border border-white/15 hover:bg-white/5"><Check className="w-3 h-3" /> Mark done</button>
                            )}
                          </div>
                          {notFound && (
                            <p className="text-[11px] text-amber-300/80 mt-1.5" data-testid={`howto-not-found-${s.index}`}>Couldn&rsquo;t find that on this screen.</p>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                </li>
              );
            })}
          </ol>

          {allDone && (
            <div className="mt-3 rounded-xl px-3 py-3 text-center" style={{ background: 'rgba(34,197,94,0.08)', border: '1px solid rgba(34,197,94,0.3)' }} data-testid="howto-all-done">
              <p className="text-[13px] font-semibold text-[#22C55E]">All done</p>
              <button type="button" data-testid="howto-all-done-close" onClick={() => closeGuide()} className="mt-2 rounded-full px-4 py-1.5 text-[12px] font-semibold text-[#0B0F1A]" style={{ background: 'linear-gradient(180deg,#E7C75E,#D4AF37)' }}>Close</button>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between gap-2 px-3.5 py-2 border-t border-white/[0.06]">
          <span className="text-[10.5px] text-white/35">Ticks itself when done</span>
          <button type="button" data-testid="howto-ask-step" onClick={() => askAboutStep(steps[currentIndex])} className="inline-flex items-center gap-1 text-[11px] text-white/50 hover:text-[#E7C96A]"><MessageCircleQuestion className="w-3.5 h-3.5" /> Ask about this step</button>
        </div>
      </div>
    </>,
    document.body,
  );
}
